import { useState, useEffect } from 'react'
import { Loader2, ExternalLink } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getSSOConfig, SSOConfig } from '../api/client'

export default function Login() {
  const { isLoading: authLoading } = useAuth()
  const [ssoConfig, setSsoConfig] = useState<SSOConfig | null>(null)
  const [ssoLoading, setSsoLoading] = useState(true)

  useEffect(() => {
    getSSOConfig()
      .then((config) => {
        setSsoConfig(config)
        // Store portal URL for logout/401 redirect
        if (config.enabled && config.portal_login_url) {
          localStorage.setItem('sso_portal_login_url', config.portal_login_url)
        }
        // Auto-redirect to portal
        if (config.portal_login_url) {
          window.location.href = config.portal_login_url
        }
      })
      .catch(() => {
        // SSO config fetch failed
      })
      .finally(() => setSsoLoading(false))
  }, [])

  if (authLoading || ssoLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F6] bg-grain">
        <Loader2 className="animate-spin" style={{ color: '#EA580C' }} size={32} />
      </div>
    )
  }

  // Show redirect message while waiting for portal redirect
  if (ssoConfig?.portal_login_url) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F6] bg-grain">
        <div className="text-center">
          <Loader2
            className="animate-spin mx-auto mb-4"
            style={{ color: '#EA580C' }}
            size={32}
          />
          <p className="font-body text-[#78716C]">Redirecting to sign-in portal...</p>
        </div>
      </div>
    )
  }

  // Fallback: SSO not configured — show split branded layout
  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left panel — dark branded side */}
      <div
        className="relative flex flex-col items-center justify-center bg-grain md:w-[42%] px-10 py-12 md:py-0 overflow-hidden"
        style={{ backgroundColor: '#1C1412' }}
      >
        {/* Orange gradient stripe at top */}
        <div className="sidebar-stripe" />

        {/* Wordmark */}
        <div className="relative z-10 text-center select-none">
          <h1
            className="font-display text-white uppercase tracking-[0.18em] text-5xl md:text-6xl leading-none"
          >
            FORGE
          </h1>
          <div
            className="w-full h-[3px] my-3 rounded-full"
            style={{ background: 'linear-gradient(90deg, #EA580C, #92400E)' }}
          />
          <h1
            className="font-display text-white uppercase tracking-[0.18em] text-5xl md:text-6xl leading-none"
          >
            QUOTE
          </h1>
          <p
            className="font-body mt-6 text-sm tracking-widest uppercase"
            style={{ color: '#A8917F' }}
          >
            Fabrication Quoting
          </p>
        </div>
      </div>

      {/* Right panel — warm cream login card */}
      <div
        className="flex flex-1 items-center justify-center bg-grain px-6 py-12"
        style={{ backgroundColor: '#FAF8F6' }}
      >
        <div className="w-full max-w-sm">
          {/* Card */}
          <div
            className="bg-white rounded-md p-8 shadow-[0_1px_4px_rgba(180,83,9,0.06)] hover:shadow-[0_4px_12px_rgba(180,83,9,0.1)] transition-shadow duration-200 border"
            style={{ borderColor: '#E7E5E4' }}
          >
            <div className="mb-6 text-center">
              <h2
                className="font-display uppercase tracking-[0.12em] text-2xl"
                style={{ color: '#292524' }}
              >
                Sign In
              </h2>
              <p className="font-body text-sm mt-1" style={{ color: '#78716C' }}>
                Access your Forge-Quote account
              </p>
            </div>

            <p className="font-body text-sm text-center mb-6" style={{ color: '#78716C' }}>
              Sign in is managed through the Forge-Quote portal.
            </p>

            <a
              href="https://portal.forge-quote.com/login"
              className="ember-glow w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md text-sm font-body font-semibold uppercase tracking-widest text-white transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: '#EA580C',
              }}
            >
              <ExternalLink size={16} />
              Sign in with Forge-Quote
            </a>
          </div>

          {/* Footer */}
          <p
            className="font-body text-center text-xs mt-6"
            style={{ color: '#A8917F' }}
          >
            &copy; Forge-Quote {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  )
}
